import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DonorSearch from './DonorSearch';
import Navbar from '../../components/Navbar';

const ANY = "Any";

const mockDonors = [
  { id: 1, donor_code: "1234", height_cm: 178, weight_kg: 79, eye_color: "Blue", hair_color: "Brown", ethnicity: "Caucasian", blood_type: "O+", education: "PhD Candidate", occupation: "Research Scientist", personality: "Analytical and curious", interests: ["Science", "Reading", "Hiking"], bio: "Passionate about genetic research and helping families grow.", available: true },
  { id: 2, donor_code: "5678", height_cm: 173, weight_kg: 73, eye_color: "Brown", hair_color: "Black", ethnicity: "Asian", blood_type: "A+", education: "MD", occupation: "Physician", personality: "Compassionate and dedicated", interests: ["Medicine", "Music", "Travel"], bio: "Dedicated doctor wanting to help others build families.", available: true },
  { id: 3, donor_code: "9012", height_cm: 183, weight_kg: 82, eye_color: "Brown", hair_color: "Black", ethnicity: "Hispanic", blood_type: "B+", education: "BS Engineering", occupation: "Engineer", personality: "Creative problem-solver", interests: ["Technology", "Sports", "Cooking"], bio: "Engineer with a passion for innovation and helping others.", available: true },
  { id: 4, donor_code: "3456", height_cm: 180, weight_kg: 84, eye_color: "Brown", hair_color: "Black", ethnicity: "African American", blood_type: "O-", education: "MBA", occupation: "Business Analyst", personality: "Strategic thinker", interests: ["Finance", "Fitness", "Photography"], bio: "Business professional focused on strategic planning and community.", available: false },
  { id: 5, donor_code: "7890", height_cm: 175, weight_kg: 77, eye_color: "Green", hair_color: "Blond", ethnicity: "Caucasian", blood_type: "A-", education: "MS Biology", occupation: "Biologist", personality: "Detail-oriented", interests: ["Nature", "Research", "Cycling"], bio: "Biologist studying ecosystems and conservation.", available: true },
  { id: 6, donor_code: "2345", height_cm: 170, weight_kg: 70, eye_color: "Brown", hair_color: "Black", ethnicity: "Asian", blood_type: "AB+", education: "PhD Physics", occupation: "Physicist", personality: "Analytical and precise", interests: ["Physics", "Chess", "Running"], bio: "Physicist exploring the fundamental laws of the universe.", available: true },
];

function unique(list) {
  return Array.from(new Set(list)).sort();
}

function Field({ k, v }) {
  return (
    <div className="flex justify-between border-b border-border/60 pb-1">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function DonorCard({ d }) {
  return (
    <Link
      to={`/donors/${d.id}`}
      className="group rounded-3xl border border-border bg-white overflow-hidden flex flex-col shadow-soft hover:shadow-large transition-all duration-300 card-hover"
      onClick={() => console.log('Clicked donor:', d.donor_code)}
    >
      <div className="aspect-[4/3] bg-gradient-to-br from-primary-50 to-blue-50 flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        <div className="relative w-24 h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-xl">
          <span className="text-5xl text-white font-bold">{d.donor_code.charAt(0)}</span>
        </div>
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <div className="font-bold text-2xl text-foreground">#{d.donor_code}</div>
          <span className={`text-xs font-semibold rounded-full px-3 py-1 ${d.available ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
            {d.available ? "Available" : "Reserved"}
          </span>
        </div>
        <div className="text-sm text-muted-foreground font-medium mb-4">{d.occupation} · {d.education}</div>

        <div className="space-y-2 text-sm mb-4">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-foreground">{d.ethnicity}</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="text-foreground">{d.eye_color} eyes, {d.hair_color} hair</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="text-foreground">{d.height_cm} cm, {d.blood_type}</span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{d.bio}</p>

        <div className="mt-auto flex flex-wrap gap-2">
          {d.interests.slice(0, 3).map((i) => (
            <span key={i} className="text-xs font-medium rounded-lg bg-primary-50 text-primary-700 px-3 py-1">{i}</span>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-primary-600 font-semibold text-sm group-hover:text-primary-700 transition-colors flex items-center">
            View Full Profile
            <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
}

function Donors() {
  const [ethnicity, setEthnicity] = useState(ANY);
  const [eye, setEye] = useState(ANY);
  const [hair, setHair] = useState(ANY);
  const [education, setEducation] = useState(ANY);
  const [blood, setBlood] = useState(ANY);
  const [minHeight, setMinHeight] = useState(160);
  const [query, setQuery] = useState("");

  const options = useMemo(() => {
    return {
      ethnicity: unique(mockDonors.map((d) => d.ethnicity)),
      eye: unique(mockDonors.map((d) => d.eye_color)),
      hair: unique(mockDonors.map((d) => d.hair_color)),
      education: unique(mockDonors.map((d) => d.education)),
      blood: unique(mockDonors.map((d) => d.blood_type)),
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return mockDonors.filter((d) => {
      if (ethnicity !== ANY && d.ethnicity !== ethnicity) return false;
      if (eye !== ANY && d.eye_color !== eye) return false;
      if (hair !== ANY && d.hair_color !== hair) return false;
      if (education !== ANY && d.education !== education) return false;
      if (blood !== ANY && d.blood_type !== blood) return false;
      if (d.height_cm < minHeight) return false;
      if (q) {
        const hay = `${d.donor_code} ${d.occupation} ${d.personality} ${d.interests.join(" ")} ${d.bio}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [ethnicity, eye, hair, education, blood, minHeight, query]);

  const resetAll = () => {
    setEthnicity(ANY);
    setEye(ANY);
    setHair(ANY);
    setEducation(ANY);
    setBlood(ANY);
    setMinHeight(160);
    setQuery("");
    console.log('Filters reset');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-blue-50 to-indigo-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center rounded-full px-4 py-1.5 text-sm font-semibold bg-primary-100 text-primary-700 mb-6 shadow-sm">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              100% Verified & FDA Compliant
            </div>
            <h1 className="font-bold text-5xl sm:text-6xl lg:text-7xl text-foreground tracking-tight mb-6">
              Find your perfect donor
            </h1>
            <p className="text-lg text-muted-foreground">
              Every profile is physician-screened and FDA-compliant. Browse detailed profiles, apply for subsidies, and connect with donors who want to help you build your family.
            </p>
            <div className="mt-6 flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="font-medium">{filtered.length} Available Donors</span>
              </div>
              <span>·</span>
              <span>{mockDonors.length} Total Profiles</span>
            </div>
          </div>
        </div>
      </section>

      {/* Donors Page */}
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-12 sm:py-16">

        <div className="mt-8 grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Filters */}
          <DonorSearch
            query={query}
            setQuery={setQuery}
            ethnicity={ethnicity}
            setEthnicity={setEthnicity}
            eye={eye}
            setEye={setEye}
            hair={hair}
            setHair={setHair}
            education={education}
            setEducation={setEducation}
            blood={blood}
            setBlood={setBlood}
            minHeight={minHeight}
            setMinHeight={setMinHeight}
            resetAll={resetAll}
            options={options}
          />

          {/* Results */}
          <div>
            {filtered.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border p-16 text-center">
                <div className="font-display text-2xl">No matches</div>
                <p className="mt-2 text-sm text-muted-foreground">Try loosening a filter or two.</p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((d) => <DonorCard key={d.id} d={d} />)}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white mt-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
          <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-slate-400 text-sm">
            <div>© {new Date().getFullYear()} The Open Sperm Bank, LLC. All rights reserved.</div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                FDA Registered
              </span>
              <span>·</span>
              <span>Physician-Supervised</span>
              <span>·</span>
              <span>HIPAA Compliant</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Donors;
